import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  DynamicHotelProps,
  GoToRoomDetailsParams,
  Hotel,
} from 'ts/hotel/interfaces/HotelResponse';
import HotelCard from './HotelCard';
import { MapHotelContext } from 'contexts/MapHotelContext';
import InfiniteScroll from 'react-infinite-scroller';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { flattenHotelsByRoom, getOriginalHotelKey, getSortedHotelsMode16 } from 'utils/hotel';
import { getHotlistFacilities } from 'services/hotel';
import { MainContext } from 'contexts/MainContext';

interface HotelsListProps {
  hotels: Hotel[];
  sortBy?: string;
  dynamic?: DynamicHotelProps;
  goToDetails: (params: GoToRoomDetailsParams) => void;
}

const HotelsList: FC<HotelsListProps> = ({ sortBy = 'recommended', ...props }) => {
  const { pinnedHotelsIds, setActiveHotel, activeHotel } = useContext(MapHotelContext);
  const count = 30;
  const { userSiteConfig, user } = useContext(MainContext);
  const mode = userSiteConfig.hotel.displayMode;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const isRoomPerHotelMode = !isMobile && mode === 16;
  const defaultAreRoomsFiltered = props.dynamic ? false : userSiteConfig.hotel.areRoomsFiltered;
  const areRoomsFiltered =
    user?.branch?.hotelDisplaySupplierName || props.dynamic ? defaultAreRoomsFiltered : true;
  const [total, setTotal] = useState(count);
  const [hasMore, setHasMore] = useState(true);
  const hotlistFacilities = getHotlistFacilities();

  const hotels = useMemo(() => {
    if (!isRoomPerHotelMode) {
      return props.hotels;
    }

    return getSortedHotelsMode16(props.hotels, sortBy, areRoomsFiltered);
  }, [areRoomsFiltered, isRoomPerHotelMode, props.hotels, sortBy]);

  const isHotelPinned = useCallback(
    (hotel: Hotel) => pinnedHotelsIds.includes(getOriginalHotelKey(hotel.htuKey)),
    [pinnedHotelsIds]
  );

  useEffect(() => {
    if (!isRoomPerHotelMode || !activeHotel) {
      return;
    }

    const needsFlattening =
      !activeHotel.htuKey.includes('__') || activeHotel.roomClasses.length > 1;

    if (!needsFlattening) {
      return;
    }

    const sourceHotel = {
      ...activeHotel,
      htuKey: getOriginalHotelKey(activeHotel.htuKey),
    };
    const flattenedActiveHotels = flattenHotelsByRoom([sourceHotel], areRoomsFiltered);
    const selectedRoomKey = activeHotel.htuKey.includes('__')
      ? activeHotel.htuKey.slice(activeHotel.htuKey.indexOf('__') + 2)
      : activeHotel.choosenRoomId;
    const nextActiveHotel =
      flattenedActiveHotels.find(
        (hotel) => hotel.roomClasses[0]?.rcUniqueKey === selectedRoomKey
      ) || flattenedActiveHotels[0];

    if (nextActiveHotel && nextActiveHotel.htuKey !== activeHotel.htuKey) {
      setActiveHotel(nextActiveHotel);
    }
  }, [activeHotel, areRoomsFiltered, isRoomPerHotelMode, setActiveHotel]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
        return;
      }

      const unpinnedHotels = hotels
        .filter((h) => !isHotelPinned(h))
        .slice(0, total);

      const scrollCardNearTop = (hotelKey: string) => {
        const card = document.getElementById(hotelKey);
        const container = document.querySelector('.hotel-list');
        if (container && card) {
          const containerRect = container.getBoundingClientRect();
          const cardRect = card.getBoundingClientRect();
          const offset = cardRect.top - containerRect.top - 100;

          if (Math.abs(offset) > 1) {
            container.scrollBy({
              top: offset,
              behavior: 'smooth',
            });
          }
        }
      };

      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      const isEditable =
        (target?.getAttribute('contenteditable') || '').toLowerCase() === 'true' ||
        target?.isContentEditable;
      if (tagName && ['input', 'textarea', 'select'].includes(tagName)) {
        return;
      }
      if (isEditable) {
        return;
      }

      if (!unpinnedHotels.length) {
        return;
      }

      if (!activeHotel) {
        if (event.key === 'ArrowDown') {
          setActiveHotel(unpinnedHotels[0]);
          scrollCardNearTop(unpinnedHotels[0].htuKey);
          event.preventDefault();
        }
        return;
      }

      const currentIndex = unpinnedHotels.findIndex((hotel) => hotel.htuKey === activeHotel.htuKey);

      if (currentIndex === -1) {
        if (event.key === 'ArrowDown') {
          setActiveHotel(unpinnedHotels[0]);
          scrollCardNearTop(unpinnedHotels[0].htuKey);
          event.preventDefault();
        }
        return;
      }

      if (event.key === 'ArrowDown' && currentIndex < unpinnedHotels.length - 1) {
        const nextHotel = unpinnedHotels[currentIndex + 1];
        setActiveHotel(nextHotel);
        scrollCardNearTop(nextHotel.htuKey);
        event.preventDefault();
      }

      if (event.key === 'ArrowUp' && currentIndex > 0) {
        const prevHotel = unpinnedHotels[currentIndex - 1];
        setActiveHotel(prevHotel);
        scrollCardNearTop(prevHotel.htuKey);
        event.preventDefault();
      }
    },
    [activeHotel, hotels, isHotelPinned, setActiveHotel, total]
  );

  useEffect(() => {
    setHasMore(hotels.length > count);
    setTotal(count);
  }, [hotels.length]);

  useEffect(() => {
    if (!activeHotel) {
      return;
    }

    const unpinned = hotels.filter((h) => !isHotelPinned(h));
    const index = unpinned.findIndex(
      (h) =>
        h.htuKey === activeHotel.htuKey ||
        getOriginalHotelKey(h.htuKey) === getOriginalHotelKey(activeHotel.htuKey),
    );
    if (index >= total) {
      setTotal(index + 1);
      setHasMore(index + 1 < unpinned.length);
    }
  }, [activeHotel, hotels, isHotelPinned, total]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const fetchData = () => {
    setTotal(total + count);
    setHasMore(total < hotels.length);
  };

  return (
    <div className="hotel-list">
      <div className="hotel-list-active" style={{ backgroundColor: 'var(--color-bg)' }}>
        {hotels
          .filter((h) => isHotelPinned(h))
          .map((hotel) => {
            return (
              <HotelCard
                hotel={hotel}
                key={hotel.htuKey}
                dynamic={props.dynamic}
                goToDetails={props.goToDetails}
                hotlistFacilities={hotlistFacilities}
              />
            );
          })}
      </div>
      <InfiniteScroll
        loadMore={fetchData}
        hasMore={hasMore}
        useWindow={false}
        loader={<div key={0}></div>}
      >
        {hotels
          .filter((h) => !isHotelPinned(h))
          .slice(0, total)
          .map((hotel) => {
            return (
              <HotelCard
                hotel={hotel}
                key={hotel.htuKey}
                dynamic={props.dynamic}
                goToDetails={props.goToDetails}
                hotlistFacilities={hotlistFacilities}
              />
            );
          })}
      </InfiniteScroll>
    </div>
  );
};
export default withErrorBoundary(HotelsList, 'HotelsList');